import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, LockKeyhole } from "lucide-react";
import { CASE_STUDIES } from "@/lib/content";

function DeviceMockup({ device, color }: { device: string; color: string }) {
  if (device === "phone") {
    return (
      <div className={`relative w-48 h-96 rounded-[2.2rem] bg-gradient-to-br ${color} p-1 ring-1 ring-white/10 shadow-2xl`}>
        <div className="h-full w-full rounded-[2rem] bg-edx-space overflow-hidden relative">
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 h-1 w-12 rounded-full bg-white/20" />
          <div className="pt-7 px-3 space-y-2">
            <div className="h-3 w-3/4 rounded bg-white/30" />
            <div className="h-3 w-1/2 rounded bg-white/15" />
            <div className="mt-4 grid grid-cols-2 gap-2">
              {Array(4).fill(0).map((_, i) => <div key={i} className="aspect-square rounded bg-white/10" />)}
            </div>
            <div className="mt-3 space-y-1.5">
              {Array(3).fill(0).map((_, i) => <div key={i} className="h-2 rounded bg-white/10" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (device === "tablet") {
    return (
      <div className={`relative w-72 h-96 rounded-2xl bg-gradient-to-br ${color} p-1 ring-1 ring-white/10 shadow-2xl`}>
        <div className="h-full w-full rounded-xl bg-edx-space p-4 overflow-hidden">
          <div className="h-3 w-2/3 rounded bg-white/30 mb-2" />
          <div className="grid grid-cols-3 gap-2 mt-3">
            {Array(6).fill(0).map((_, i) => <div key={i} className="aspect-video rounded bg-white/10" />)}
          </div>
          <div className="mt-3 h-32 rounded bg-white/5 p-3 flex items-end gap-1">
            {Array(8).fill(0).map((_, i) => (
              <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-edx-cyan to-edx-purple" style={{ height: `${30 + Math.random() * 60}%` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={`relative w-full max-w-[420px] h-72 rounded-xl bg-gradient-to-br ${color} p-1 ring-1 ring-white/10 shadow-2xl`}>
      <div className="h-full w-full rounded-lg bg-edx-space p-4 overflow-hidden">
        <div className="flex items-center gap-1.5 mb-3">
          <div className="h-2.5 w-2.5 rounded-full bg-edx-pink/60" />
          <div className="h-2.5 w-2.5 rounded-full bg-edx-orange/60" />
          <div className="h-2.5 w-2.5 rounded-full bg-edx-green/60" />
        </div>
        <div className="h-3 w-1/2 rounded bg-white/30 mb-3" />
        <div className="grid grid-cols-4 gap-2 mb-3">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="h-16 rounded bg-white/5 p-2">
              <div className="h-2 w-3/4 rounded bg-white/20 mb-1" />
              <div className="h-3 w-1/2 rounded bg-gradient-to-r from-edx-cyan to-edx-purple" />
            </div>
          ))}
        </div>
        <div className="h-28 rounded bg-white/5 p-3 flex items-end gap-1">
          {Array(12).fill(0).map((_, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-edx-blue via-edx-purple to-edx-pink" style={{ height: `${20 + Math.random() * 70}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CaseStudies() {
  return (
    <section id="case-studies" data-testid="case-studies" className="relative py-24">
      <div className="container-edx">
        <div className="max-w-3xl">
          <div className="section-index">EDX.04</div>
          <h2 className="display mt-4 text-4xl sm:text-5xl md:text-6xl text-white">
            Case studies that <span className="gradient-text">prove it</span>.
          </h2>
          <p className="mt-5 text-edx-muted text-base md:text-lg max-w-2xl">
            Real projects, real impact. Hover the cards to see what we ship.
          </p>
        </div>

        <div className="mt-16 space-y-20">
          {CASE_STUDIES.map((c, i) => (
            <motion.div
              key={c.title}
              data-testid={`case-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${i % 2 ? "lg:flex-row-reverse" : ""}`}
            >
              <div className={`${i % 2 ? "lg:order-2" : ""}`}>
                <div className="text-[11px] uppercase tracking-widest text-edx-cyan font-mono">{c.category}</div>
                <h3 className="display mt-3 text-3xl md:text-4xl text-white">{c.title}</h3>
                <p className="mt-4 text-edx-muted leading-relaxed">{c.desc}</p>
                {c.title === "NOVA E-Hailing" && (
                  <div className="mt-4 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-edx-orange">
                    <LockKeyhole size={13} /> Strategic details intentionally confidential
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-2">
                  {c.tech.map((t) => (
                    <span key={t} className="text-[11px] font-mono px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-edx-muted">
                      {t}
                    </span>
                  ))}
                </div>
                <Link to="/portfolio" className="btn btn-ghost mt-7 !text-sm">
                  View full case study <ArrowRight size={14} />
                </Link>
              </div>
              <div className={`flex justify-center ${i % 2 ? "lg:order-1" : ""}`}>
                <motion.div className="w-full flex justify-center" whileHover={{ scale: 1.04, rotate: i % 2 ? 2 : -2 }} transition={{ duration: 0.5 }}>
                  <DeviceMockup device={c.device} color={c.color} />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
